import * as Tabs from '@radix-ui/react-tabs';
import { ampli } from '@/ampli';
import { Close } from '@radix-ui/react-dialog';
import { useContext, type ReactNode } from 'react';

import { ClientInputError } from '@/lib/errors/client';

import { RefreshButton } from '@/components/TransactionModule/SelectTrade/RefreshButton';
import { TransactionModuleContainerContext } from '@/components/TransactionModule/TransactionModuleContainer';
import { useUpdateRouteTradeModuleStep } from '@/components/TransactionModule/utils';

import { isTriggerOrderType, type AdvancedOrderType } from '@/constants/advancedOrder';
import { CHAIN_IDS } from '@/constants/chain';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { type CheckoutMode, CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useIntentsAvailability } from '@/hooks/useIntentsAvailability';

import {
  hasAlteredLiquiditySourcesForChainSelector,
  useLiquiditySourcesStore,
} from '@/store/liquiditySources';
import {
  hasAlteredSettingsSelector,
  hasAlteredSlippageSettingsSelector,
  matchaAutoSelectors,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { buttons } from '@/styles/buttons';
import { tabsStyles } from '@/styles/tabs';

import { NotificationIndicatorIcon } from '@/ui/Icons/NotificationIndicatorIcon';
import { SettingsIcon } from '@/ui/Icons/SettingsIcon';
import { XIcon } from '@/ui/Icons/XIcon';

import { toAmpliSwapOrderType } from '@/utils/advancedOrderAnalytics';
import { classNames } from '@/utils/classnames';
import { isTokenAddressNativeAsset } from '@/utils/multichain';

import { useModuleContext } from '../ModuleContext';
import { DEFAULT_TOKEN_INPUT, useTradeModuleStore, type TradeModuleStore } from '../store';
import { useCheckout } from '../useCheckout';

const { container, tab } = tabsStyles({ variant: 'card' });

const modeTabsStoreSelector = (state: TradeModuleStore) => ({
  updateBuyInput: state.updateBuyInput,
  sellInputDecimal: state.sellInput.decimal,
});

type ModeTabsProps = {
  children: ReactNode;
};

type TradeModuleTab = 'swap' | 'limit' | 'advanced';

function resolveTradeModuleTab(
  checkoutMode: CheckoutMode,
  orderType: AdvancedOrderType,
): TradeModuleTab {
  if (checkoutMode !== CheckoutModes.advanced) return 'swap';
  return isTriggerOrderType(orderType) ? 'advanced' : 'limit';
}

/** Renders URL-controlled Swap, Limit, and Advanced selectors around the root-routed panel. */
export function ModeTabs({ children }: ModeTabsProps) {
  const { variant, sellToken, buyToken, setSettingsOpen } = useModuleContext();
  const { modal: isInModal } = useContext(TransactionModuleContainerContext);
  const chainId = sellToken?.chainId ?? CHAIN_IDS.MAINNET;
  const [rawCheckoutMode, setCheckoutMode] = useCheckoutMode();
  const [advancedOrderType, setAdvancedOrderType] = useAdvancedOrderType();
  const checkoutMode =
    rawCheckoutMode === CheckoutModes.crossChain ? CheckoutModes.market : rawCheckoutMode;
  const tradeTab = resolveTradeModuleTab(checkoutMode, advancedOrderType);
  const hasAlteredMarketTradeSettings = useMarketTradeSettingsStore(hasAlteredSettingsSelector);
  const hasAlteredCrossChainSettings = useMarketTradeSettingsStore(
    hasAlteredSlippageSettingsSelector,
  );
  const { isMatchaAutoOn } = useMarketTradeSettingsStore(matchaAutoSelectors);
  const hasAlteredLiquiditySources = useLiquiditySourcesStore(
    hasAlteredLiquiditySourcesForChainSelector(chainId),
  );
  const isIntentsEnabledForCurrentTrade = useIntentsAvailability({
    buyToken,
    sellToken,
    chainId,
  }).isEnabled;
  const { updateBuyInput, sellInputDecimal } = useTradeModuleStore(modeTabsStoreSelector);
  const [, dispatchCheckout] = useCheckout();
  const updateRouteTradeModuleStep = useUpdateRouteTradeModuleStep();

  const isCrossChainTokenPair =
    sellToken != null && buyToken != null && sellToken.chainId !== buyToken.chainId;

  const shouldShowCustomSettingsDot = isCrossChainTokenPair
    ? hasAlteredCrossChainSettings
    : isIntentsEnabledForCurrentTrade
      ? !isMatchaAutoOn
      : hasAlteredMarketTradeSettings || hasAlteredLiquiditySources;

  const handleTabChange = (value: CheckoutMode) => {
    ampli.orderTypeTabSelected({
      swap_order_type: toAmpliSwapOrderType(value),
    });
    setCheckoutMode(value);
  };

  const handleClickTradeSettings = () => {
    setSettingsOpen(true);
    ampli.tradeSettingsOpened({
      swap_order_type: toAmpliSwapOrderType(rawCheckoutMode),
      swap_execution_type:
        isMatchaAutoOn && !isTokenAddressNativeAsset(sellToken?.address, sellToken?.chainId)
          ? 'gasless'
          : 'standard',
    });
  };

  const handleTradeTabChange = (value: string) => {
    const tab = value as TradeModuleTab;
    if (tab === 'swap') {
      if (checkoutMode !== CheckoutModes.market) {
        updateBuyInput(DEFAULT_TOKEN_INPUT);
      }
      handleTabChange(CheckoutModes.market);
      return;
    }

    if (tab === 'limit' || tab === 'advanced') {
      if (checkoutMode !== CheckoutModes.advanced) {
        updateBuyInput(DEFAULT_TOKEN_INPUT);
      }
      const nextOrderType =
        tab === 'limit'
          ? 'limit'
          : isTriggerOrderType(advancedOrderType)
            ? advancedOrderType
            : 'stop-loss';
      ampli.orderTypeTabSelected({
        swap_order_type: toAmpliSwapOrderType(CheckoutModes.advanced, nextOrderType),
      });
      if (tab === 'limit') {
        setAdvancedOrderType('limit');
      } else if (!isTriggerOrderType(advancedOrderType)) {
        setAdvancedOrderType('stop-loss');
      }
      return;
    }

    throw new ClientInputError(`Change value is not a trade tab: ${tab}`);
  };

  const handleClose = () => {
    dispatchCheckout({ type: 'reset' });
    updateRouteTradeModuleStep(undefined);
  };

  const tabClassName = classNames('outline-transparent tablet:pt-0', {
    'pt-15': variant === 'fullpage',
  });
  const fullpageClassName = [
    'fixed rounded-none bg-special-background-modal border-b tablet:pr-2.5',
    'tablet:static tablet:rounded-2xl tablet:bg-transparent tablet:border-b-0',
  ].join(' ');

  return (
    <Tabs.Root value={tradeTab} onValueChange={handleTradeTabChange} className="mb-6">
      <div
        className={classNames(
          'flex justify-between items-center p-5 py-0 z-10',
          'border-token-gray-300 w-full h-[60px]',
          { [`${fullpageClassName}`]: variant === 'fullpage' },
          { 'rounded-2xl pr-2.5': variant === 'widget' },
        )}
      >
        <Tabs.List aria-label="order type tabs" className={container()}>
          <Tabs.Trigger className={tab({ class: 'text-[16px] font-medium' })} value="swap">
            Swap
          </Tabs.Trigger>
          <Tabs.Trigger className={tab({ class: 'text-[16px] font-medium' })} value="limit">
            Limit
          </Tabs.Trigger>
          <Tabs.Trigger className={tab({ class: 'text-[16px] font-medium' })} value="advanced">
            Advanced
          </Tabs.Trigger>
        </Tabs.List>

        <div className="flex items-center">
          {checkoutMode !== CheckoutModes.advanced && (
            <>
              <RefreshButton sellInputDecimal={sellInputDecimal} />
              <button
                aria-label="trade settings"
                className={buttons({
                  size: 'small',
                  iconOnly: true,
                  shape: 'rounded',
                  color: 'transparent',
                  class: ['relative', variant === 'fullpage' ? 'mr-2 tablet:mr-0' : 'mr-1'],
                })}
                type="button"
                onClick={handleClickTradeSettings}
              >
                {shouldShowCustomSettingsDot && (
                  <NotificationIndicatorIcon className="absolute top-1.5 right-1.25" />
                )}
                <SettingsIcon />
              </button>
            </>
          )}
          {isInModal && (
            <Close
              aria-label="close trade module"
              className={classNames(
                'focus-visible:outline-none',
                'desktop:focus-visible:outline-3',
                'desktop:focus-visible:outline-token-gray-500',
              )}
              onClick={handleClose}
            >
              <XIcon className="size-4" />
            </Close>
          )}
        </div>
      </div>

      <Tabs.Content
        className={tabClassName}
        value={tradeTab}
        tabIndex={tradeTab === 'swap' ? -1 : undefined}
      >
        {children}
      </Tabs.Content>
    </Tabs.Root>
  );
}
